import { Reducer, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { IssueItem } from "@ServerTypes/issues/issues.schema";

type IssuesState = Record<string, IssueItem | undefined>;

// stored issues always carry `id` (optional on the shared server IssueItem). Derived from IssueItem
// rather than re-declared as `string`, so the branded ObjectId type is preserved for consumers.
type StoredIssue = IssueItem & { id: NonNullable<IssueItem["id"]> };

const initialIssuesState: IssuesState = {};

const issuesSlice = createSlice({
  name: "issues",
  initialState: initialIssuesState,
  reducers: {
    setIssuesData: (state: IssuesState, action: PayloadAction<IssueItem[]>) => {
      for (const issue of action.payload as StoredIssue[]) {
        if (state[issue.id]) {
          state[issue.id] = {
            ...state[issue.id],
            ...issue,
          };
        } else {
          state[issue.id] = issue;
        }
      }
    },
    updateIssuesData: (_state: IssuesState, action: PayloadAction<IssueItem[]>) =>
      action.payload.reduce<IssuesState>((prev, curr) => {
        if (curr && curr.id) {
          return { ...prev, [curr.id]: curr };
        }
        return { ...prev };
      }, {}),
    modifyIssuesData: (state: IssuesState, action: PayloadAction<IssueItem>) => {
      const issue = action.payload as StoredIssue;
      state[issue.id] = {
        ...state[issue.id],
        ...issue,
      };
    },
    setIssuesCommentsData: (state: IssuesState, action: PayloadAction<any>) => {
      const { issueId, data, isEdit } = action.payload;
      const issueData = state[issueId] || {};
      let commentsData = {};
      if (Array.isArray(data)) {
        commentsData = data.reduce((prev: any, curr: any) => ({ ...prev, [curr._id]: curr }), {});
      } else if (typeof data === "object") {
        if (issueData.comments) {
          commentsData = isEdit
            ? {
                ...issueData.comments,
                [data._id]: {
                  ...issueData.comments[data._id],
                  ...data,
                },
              }
            : {
                [data._id]: data,
                ...issueData.comments,
              };
        } else {
          commentsData = {
            [data._id]: {
              ...data,
            },
          };
        }
      }
      state[issueId] = { ...issueData, comments: commentsData };
    },
    deleteIssuesCommentsData: (state: IssuesState, action: PayloadAction<any>) => {
      const { issueId, commentId } = action.payload;
      const issue = state[issueId];
      if (issue && issue.comments) {
        delete issue.comments[commentId];
      }
      state[issueId] = issue;
    },
    emptyIssuesData: () => ({}),
    mergeIssuesData: (state: IssuesState, action: PayloadAction<{ data: IssueItem; editId?: string }>) => {
      const { data, editId } = action.payload;
      const issueId = (data as StoredIssue).id;
      if (editId) {
        state[editId] = { ...state[editId], ...data };
      } else {
        return { [issueId]: { ...data }, ...state };
      }
    },
    removeIssueFromState: (state: IssuesState, action: PayloadAction<{ ids: string[] }>) => {
      const { ids } = action.payload;
      ids.forEach((id: string) => {
        delete state[id];
      });
    },
    deleteIssuesData: (state: IssuesState, action: PayloadAction<string[]>) => {
      const issues = action.payload;
      issues.forEach((issue) => {
        delete state[issue];
      });
    },
    initializeIssuesData: () => initialIssuesState,
  },
});

export const {
  updateIssuesData,
  setIssuesData,
  modifyIssuesData,
  setIssuesCommentsData,
  deleteIssuesCommentsData,
  emptyIssuesData,
  mergeIssuesData,
  removeIssueFromState,
  deleteIssuesData,
  initializeIssuesData,
} = issuesSlice.actions;

const issueReducer: Reducer<IssuesState> = issuesSlice.reducer;
export default issueReducer;
